import React, { useState, useEffect, useReducer, useContext } from "react";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import { Avatar, CircularProgress } from "@material-ui/core";
import {
  DeleteOutline,
  Edit,
  PersonOutline,
  Add,
  Search as SearchIcon,
  ChatBubbleOutline,
  VpnKeyOutlined,
} from "@material-ui/icons";
import { useHistory } from "react-router-dom";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { getBackendUrl } from "../../config";
import usePlans from "../../hooks/usePlans";

import UserModal from "../../components/UserModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import ForbiddenPage from "../../components/ForbiddenPage";
import UserStatusIcon from "../../components/UserModal/statusIcon";
import PermissionsModal from "../../components/PermissionsModal";
import whatsappIcon from "../../assets/nopicture.png";

const backendUrl = getBackendUrl();

const reducer = (state, action) => {
  if (action.type === "LOAD_USERS") {
    const users = action.payload;
    const newUsers = [];

    users.forEach((user) => {
      const userIndex = state.findIndex((u) => u.id === user.id);
      if (userIndex !== -1) {
        state[userIndex] = user;
      } else {
        newUsers.push(user);
      }
    });

    return [...state, ...newUsers];
  }

  if (action.type === "UPDATE_USERS") {
    const user = action.payload;
    const userIndex = state.findIndex((u) => u.id === user.id);

    if (userIndex !== -1) {
      state[userIndex] = user;
      return [...state];
    } else {
      return [user, ...state];
    }
  }

  if (action.type === "DELETE_USER") {
    const userId = action.payload;

    const userIndex = state.findIndex((u) => u.id === userId);
    if (userIndex !== -1) {
      state.splice(userIndex, 1);
    }
    return [...state];
  }

  if (action.type === "RESET") {
    return [];
  }
};

const useStyles = makeStyles((theme) => ({
  container: {
    padding: "24px",
    background: "#f5f7fa",
    minHeight: "100vh",
  },
  header: {
    marginBottom: "24px",
  },
  headerTop: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "16px",
    flexWrap: "wrap",
    gap: "16px",
  },
  title: {
    fontSize: "24px",
    fontWeight: 700,
    color: "#1a1a1a",
    margin: 0,
    display: "flex",
    alignItems: "center",
    gap: "12px",
  },
  filterContainer: {
    background: "white",
    borderRadius: "8px",
    padding: "20px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    border: "1px solid #e8ecf1",
    marginBottom: "24px",
  },
  filterTitle: {
    fontSize: "14px",
    fontWeight: 700,
    color: "#1a1a1a",
    marginBottom: "16px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  },
  filterRow: {
    display: "grid",
    gridTemplateColumns: "1fr auto",
    gap: "16px",
    alignItems: "center",
  },
  headerActions: {
    display: "flex",
    gap: "12px",
    alignItems: "center",
    flexWrap: "wrap",
  },
  searchContainer: {
    position: "relative",
    display: "flex",
    alignItems: "center",
  },
  searchInput: {
    padding: "10px 12px 10px 40px",
    border: "1px solid #e8ecf1",
    borderRadius: "6px",
    fontSize: "13px",
    fontFamily: "inherit",
    background: "#fafbfc",
    transition: "all 0.2s ease",
    width: "100%",
    "&:focus": {
      outline: "none",
      borderColor: theme.palette.primary.main,
      background: "white",
      boxShadow: `0 0 0 3px ${theme.palette.primary.main}20`,
    },
  },
  searchIcon: {
    position: "absolute",
    left: "12px",
    color: "#666",
    fontSize: "20px",
  },
  addButton: {
    padding: "10px 20px",
    background: theme.palette.primary.main,
    color: "white",
    border: "none",
    borderRadius: "6px",
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.3s ease",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    "&:hover": {
      background: theme.palette.primary.dark,
      transform: "translateY(-1px)",
      boxShadow: "0 2px 8px rgba(59, 130, 246, 0.3)",
    },
  },
  secondaryButton: {
    padding: "10px 20px",
    background: theme.palette.secondary.main,
    color: "white",
    border: "none",
    borderRadius: "6px",
    fontSize: "13px",
    fontWeight: 600,
    cursor: "pointer",
    transition: "all 0.3s ease",
    display: "flex",
    alignItems: "center",
    gap: "6px",
    "&:hover": {
      background: theme.palette.secondary.dark,
      transform: "translateY(-1px)",
    },
    "&:disabled": {
      background: "#cbd5e1",
      cursor: "not-allowed",
      transform: "none",
    },
  },
  tableCard: {
    background: "white",
    borderRadius: "8px",
    padding: "20px",
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    border: "1px solid #e8ecf1",
    overflow: "hidden",
  },
  tableContainer: {
    overflowX: "auto",
    maxHeight: "calc(100vh - 250px)",
    overflowY: "auto",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  tableHeader: {
    background: "#f9fafb",
    borderBottom: "2px solid #e8ecf1",
    position: "sticky",
    top: 0,
    zIndex: 1,
  },
  tableHeaderCell: {
    padding: "12px",
    textAlign: "center",
    fontSize: "12px",
    fontWeight: 600,
    color: "#666",
    whiteSpace: "nowrap",
  },
  tableRow: {
    borderBottom: "1px solid #e8ecf1",
    transition: "background 0.2s ease",
    "&:hover": {
      background: "#f9fafb",
    },
  },
  tableCell: {
    padding: "12px",
    fontSize: "13px",
    color: "#1a1a1a",
    textAlign: "center",
  },
  actionButtons: {
    display: "flex",
    gap: "6px",
    justifyContent: "center",
  },
  actionButton: {
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: "6px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "4px",
    transition: "all 0.2s ease",
    "&:hover": {
      background: "#f0f0f0",
    },
    "&:disabled": {
      opacity: 0.5,
      cursor: "not-allowed",
    },
  },
  userAvatar: {
    width: "40px",
    height: "40px",
  },
  profileBadge: {
    display: "inline-block",
    padding: "4px 12px",
    borderRadius: "20px",
    fontSize: "11px",
    fontWeight: 600,
    textTransform: "capitalize",
  },
  profileAdmin: {
    background: "#dbeafe",
    color: "#1e40af",
  },
  profileUser: {
    background: "#fce7f3",
    color: "#831843",
  },
  emptyState: {
    textAlign: "center",
    padding: "40px 20px",
    color: "#999",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    padding: "40px 20px",
    gap: "12px",
  },
  loadMoreButton: {
    width: "100%",
    padding: "12px",
    marginTop: "16px",
    background: "#f9fafb",
    border: "1px solid #e8ecf1",
    borderRadius: "6px",
    fontSize: "13px",
    fontWeight: 600,
    color: "#666",
    cursor: "pointer",
    transition: "all 0.2s ease",
    "&:hover": {
      background: "#f0f0f0",
      borderColor: theme.palette.primary.main,
      color: theme.palette.primary.main,
    },
  },
}));

const Users = () => {
  const classes = useStyles();
  const history = useHistory();
  const { user: loggedInUser, socket } = useContext(AuthContext);
  const { getPlanCompany } = usePlans();

  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [permissionsModalOpen, setPermissionsModalOpen] = useState(false);
  const [selectedUserForPermissions, setSelectedUserForPermissions] = useState(null);
  const [searchParam, setSearchParam] = useState("");
  const [users, dispatch] = useReducer(reducer, []);
  const [showInternalChat, setShowInternalChat] = useState(false);

  const companyId = loggedInUser.companyId;

  useEffect(() => {
    async function fetchData() {
      if (!companyId) return;

      try {
        setLoading(true);
        const planConfigs = await getPlanCompany(undefined, companyId);
        setShowInternalChat(planConfigs.plan.useInternalChat);
      } catch (err) {
        console.error("Error fetching plan:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    setLoading(true);
    const fetchUsers = async () => {
      try {
        const { data } = await api.get("/users/", {
          params: { searchParam, pageNumber },
        });
        dispatch({ type: "LOAD_USERS", payload: data.users });
        setHasMore(data.hasMore);
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    };
    fetchUsers();
  }, [searchParam, pageNumber]);

  useEffect(() => {
    if (loggedInUser) {
      const companyId = loggedInUser.companyId;

      const onCompanyUser = (data) => {
        if (data.action === "update" || data.action === "create") {
          dispatch({ type: "UPDATE_USERS", payload: data.user });
        }
        if (data.action === "delete") {
          dispatch({ type: "DELETE_USER", payload: +data.userId });
        }
      };

      socket.on(`company-${companyId}-user`, onCompanyUser);

      return () => {
        socket.off(`company-${companyId}-user`, onCompanyUser);
      };
    }
  }, [socket, loggedInUser]);

  const handleOpenUserModal = () => {
    setSelectedUser(null);
    setUserModalOpen(true);
  };

  const handleCloseUserModal = () => {
    setSelectedUser(null);
    setUserModalOpen(false);
  };

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleEditUser = (user) => {
    setSelectedUser(user);
    setUserModalOpen(true);
  };

  const handleDeleteUser = async (userId) => {
    try {
      await api.delete(`/users/${userId}`);
      toast.success(i18n.t("users.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingUser(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const loadMore = () => {
    setLoadingMore(true);
    setPageNumber((prevPage) => prevPage + 1);
  };

  const renderProfileImage = (user) => {
    const buildImageUrl = (userData) => {
      if (!userData.profileImage) {
        return whatsappIcon;
      }
      return `${backendUrl}/public/company${userData.companyId}/user/${userData.profileImage}`;
    };

    if (user.id === loggedInUser.id) {
      const savedProfileImage = localStorage.getItem("profileImage");
      const profileImageToUse = savedProfileImage || user.profileImage;

      const imageUrl = profileImageToUse
        ? `${backendUrl}/public/company${user.companyId}/user/${profileImageToUse}`
        : whatsappIcon;

      return (
        <Avatar
          src={imageUrl}
          alt={user.name}
          className={classes.userAvatar}
          onError={(e) => {
            e.target.src = whatsappIcon;
          }}
        />
      );
    }

    return (
      <Avatar
        src={buildImageUrl(user)}
        alt={user.name}
        className={classes.userAvatar}
      />
    );
  };

  const handleCreateChat = async (targetUser) => {
    try {
      setLoading(true);
      const { data } = await api.post("/chats", {
        users: [{ id: loggedInUser.id }, { id: targetUser.id }],
        isGroup: false,
        title: targetUser.name,
      });
      toast.success("Chat criado com sucesso!");
      history.push(`/chats/${data.uuid}`);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleBackfillChats = async () => {
    if (
      !window.confirm(
        "Tem certeza que deseja gerar chats para todos os usuários existentes? Isso pode demorar e criar muitos chats."
      )
    ) {
      return;
    }
    setLoading(true);
    try {
      await api.post("/chats/backfill");
      toast.success("Chats gerados com sucesso!");
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenPermissionsModal = (user) => {
    // Apenas admins podem gerenciar permissões
    // Admins podem alterar permissões de usuários e de outros admins
    setSelectedUserForPermissions(user);
    setPermissionsModalOpen(true);
  };

  const handleClosePermissionsModal = () => {
    setSelectedUserForPermissions(null);
    setPermissionsModalOpen(false);
  };

  const handleSavePermissions = async (permissions) => {
    try {
      console.log("Permissões recebidas para salvar:", permissions);
      console.log("Usuário selecionado:", selectedUserForPermissions);
      
      // As permissões já vêm no formato correto do PermissionsModal
      // allowGroup e finalizacaoComValorVendaAtiva são boolean
      // Os outros são string "enabled"/"disabled"
      const permissionsToSave = {
        allHistoric: permissions.allHistoric,
        allUserChat: permissions.allUserChat,
        userClosePendingTicket: permissions.userClosePendingTicket,
        showDashboard: permissions.showDashboard,
        allowRealTime: permissions.allowRealTime,
        allowConnections: permissions.allowConnections,
        showContacts: permissions.showContacts,
        showCampaign: permissions.showCampaign,
        showFlow: permissions.showFlow,
        allowSeeMessagesInPendingTickets: permissions.allowSeeMessagesInPendingTickets,
        allowGroup: permissions.allowGroup,
        finalizacaoComValorVendaAtiva: permissions.finalizacaoComValorVendaAtiva,
      };
      
      console.log("Enviando para API (tipos corretos):", permissionsToSave);
      
      await api.put(`/users/${selectedUserForPermissions.id}`, permissionsToSave);
      toast.success(i18n.t("users.toasts.updated"));
      
      // Atualizar o estado local com os dados corretos, preservando queues
      dispatch({
        type: "UPDATE_USERS",
        payload: { 
          ...selectedUserForPermissions, 
          ...permissionsToSave,
          queues: selectedUserForPermissions.queues 
        },
      });
      
      handleClosePermissionsModal();
    } catch (err) {
      toastError(err);
    }
  };

  if (loggedInUser.profile === "user") {
    return <ForbiddenPage />;
  }

  return (
    <div className={classes.container}>
      <ConfirmationModal
        title={
          deletingUser &&
          `${i18n.t("users.confirmationModal.deleteTitle")} ${deletingUser.name}?`
        }
        open={confirmModalOpen}
        onClose={() => setConfirmModalOpen(false)}
        onConfirm={() => handleDeleteUser(deletingUser.id)}
      >
        {i18n.t("users.confirmationModal.deleteMessage")}
      </ConfirmationModal>

      <UserModal
        open={userModalOpen}
        onClose={handleCloseUserModal}
        aria-labelledby="form-dialog-title"
        userId={selectedUser && selectedUser.id}
      />

      <PermissionsModal
        open={permissionsModalOpen}
        onClose={handleClosePermissionsModal}
        user={selectedUserForPermissions}
        onSave={handleSavePermissions}
      />

      {/* Header */}
      <div className={classes.header}>
        <div className={classes.headerTop}>
          <h1 className={classes.title}>
            <PersonOutline style={{ fontSize: "28px", color: "#06b6d4" }} />
            {i18n.t("users.title")}
          </h1>
          <div className={classes.headerActions}>
            <button className={classes.addButton} onClick={handleOpenUserModal}>
              <Add style={{ fontSize: "20px" }} />
            </button>
          </div>
        </div>

        {/* Filtros */}
        <div className={classes.filterContainer}>
          <div className={classes.filterTitle}>
            <SearchIcon style={{ fontSize: "18px" }} />
            Buscar Usuários
          </div>
          <div className={classes.filterRow}>
            <div className={classes.searchContainer}>
              <SearchIcon className={classes.searchIcon} />
              <input
                type="text"
                placeholder={i18n.t("contacts.searchPlaceholder")}
                value={searchParam}
                onChange={handleSearch}
                className={classes.searchInput}
              />
            </div>
            <div style={{ fontSize: "13px", color: "#666", whiteSpace: "nowrap" }}>
              {users.length} {users.length === 1 ? "usuário" : "usuários"}
            </div>
          </div>
        </div>
      </div>

      {/* Tabela */}
      <div className={classes.tableCard}>
        {users.length === 0 && !loading ? (
          <div className={classes.emptyState}>
            <p>Nenhum usuário encontrado</p>
          </div>
        ) : (
          <>
            <div className={classes.tableContainer}>
              <table className={classes.table}>
                <thead className={classes.tableHeader}>
                  <tr>
                    <th className={classes.tableHeaderCell}>
                      {i18n.t("users.table.ID")}
                    </th>
                    <th className={classes.tableHeaderCell}>
                      {i18n.t("users.table.status")}
                    </th>
                    <th className={classes.tableHeaderCell}>Avatar</th>
                    <th className={classes.tableHeaderCell}>
                      {i18n.t("users.table.name")}
                    </th>
                    <th className={classes.tableHeaderCell}>
                      {i18n.t("users.table.email")}
                    </th>
                    <th className={classes.tableHeaderCell}>
                      {i18n.t("users.table.profile")}
                    </th>
                    <th className={classes.tableHeaderCell}>
                      {i18n.t("users.table.actions")}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((user) => (
                    <tr key={user.id} className={classes.tableRow}>
                      <td className={classes.tableCell}>{user.id}</td>
                      <td className={classes.tableCell}>
                        <UserStatusIcon user={user} />
                      </td>
                      <td className={classes.tableCell}>
                        {renderProfileImage(user)}
                      </td>
                      <td className={classes.tableCell}>{user.name}</td>
                      <td className={classes.tableCell}>{user.email}</td>
                      <td className={classes.tableCell}>
                        <span
                          className={`${classes.profileBadge} ${
                            user.profile === "admin"
                              ? classes.profileAdmin
                              : classes.profileUser
                          }`}
                        >
                          {user.profile}
                        </span>
                      </td>
                      <td className={classes.tableCell}>
                        <div className={classes.actionButtons}>
                          <button
                            className={classes.actionButton}
                            onClick={() => handleCreateChat(user)}
                            disabled={!showInternalChat}
                            title="Criar Chat"
                          >
                            <ChatBubbleOutline
                              style={{ fontSize: "18px", color: "#10b981" }}
                            />
                          </button>
                          <button
                            className={classes.actionButton}
                            onClick={() => handleOpenPermissionsModal(user)}
                            title="Permissões"
                            style={{ display: loggedInUser.profile === "admin" ? "block" : "none" }}
                          >
                            <VpnKeyOutlined
                              style={{ fontSize: "18px", color: "#f59e0b" }}
                            />
                          </button>
                          <button
                            className={classes.actionButton}
                            onClick={() => handleEditUser(user)}
                            title="Editar"
                          >
                            <Edit style={{ fontSize: "18px", color: "#3b82f6" }} />
                          </button>
                          <button
                            className={classes.actionButton}
                            onClick={() => {
                              setConfirmModalOpen(true);
                              setDeletingUser(user);
                            }}
                            title="Deletar"
                          >
                            <DeleteOutline
                              style={{ fontSize: "18px", color: "#ef4444" }}
                            />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {loading && !loadingMore && (
              <div className={classes.loadingContainer}>
                <CircularProgress size={24} />
                <span>{i18n.t("loading")}</span>
              </div>
            )}
            {hasMore && !loading && (
              <button
                className={classes.loadMoreButton}
                onClick={loadMore}
                disabled={loadingMore}
              >
                {loadingMore ? "Carregando..." : "Carregar mais"}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default Users;
